/* eslint-disable import/no-default-export */
import React, {useState, useEffect, useCallback} from 'react';
import classnames from 'classnames';
import style from '@dazn/lp-web/src/components/Images/ImageComponent/image.module.css';
import {IImage} from '@dazn/lp-web/src/types';
import {ComponentTypes} from '@dazn/lp-web/src/constants/componentTypes.constants';

const PLACEHOLDER_URL =
    'https://static.dazndn.com/chapters/web/catalog/dazn-placeholder_v1.gif';

interface StaticImageProps {
    alt?: string;
    image?: IImage;
    forwardRef?: React.RefObject<HTMLDivElement>;
    onImageError?: () => void;
    onImageLoad?: () => void;
    placeholder?: string;
    componentType?: string;
    showPlaceHolderImage?: boolean;
}

const StaticImage: React.FC<StaticImageProps> = ({
    alt = '',
    image = null,
    forwardRef = null,
    onImageError = () => null,
    onImageLoad = () => null,
    placeholder = PLACEHOLDER_URL,
    componentType = null,
    showPlaceHolderImage = false
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        setHasError(false);
    }, [image?.src]);

    const handleImageLoad = useCallback(() => {
        setIsLoading(false);
        onImageLoad();
    }, [onImageLoad]);

    const handleImageError = useCallback(() => {
        setHasError(true);
        onImageError();
    }, [onImageError]);

    return (
        <div
            className={classnames(style.wrapper, {
                [style.spotlightBackground]:
                    componentType === ComponentTypes.SPOTLIGHT_RAIL,
                [style.freemiumBackground]:
                    componentType === ComponentTypes.FREEMIUM_BANNER,
                [style.placeHolderImage]: isLoading && showPlaceHolderImage,
                [style.featureBackground]:
                    componentType === ComponentTypes.FEATURES_V2
            })}
            style={{
                backgroundImage: `${
                    showPlaceHolderImage && isLoading
                        ? `url(${placeholder})`
                        : 'none'
                }`
            }}
            ref={forwardRef}
        >
            <picture>
                {image?.mobile && (
                    <source
                        media={`(max-width: 767px)`}
                        srcSet={image.mobile}
                    />
                )}
                {image?.tablet && (
                    <source
                        media={`(max-width: 1024px)`}
                        srcSet={image.tablet}
                    />
                )}
                {image?.web && (
                    <source media={`(max-width: 1919px)`} srcSet={image.web} />
                )}
                {image?.livingRoom && (
                    <source
                        media={`(min-width: 1920px)`}
                        srcSet={image.livingRoom}
                    />
                )}
                {image?.default && !hasError && (
                    <img
                        className={classnames(style.image, {
                            [style.hasLoaded]: !isLoading
                        })}
                        alt={alt}
                        src={image.default}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                    />
                )}

                <img
                    className={classnames(style.image, {
                        [style.hasLoaded]: !isLoading
                    })}
                    alt={alt}
                    src={image?.default} // Default or fallback image
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                />
            </picture>
        </div>
    );
};

export default StaticImage;
